import type {
  ClientId,
  IMessage,
  MessageType,
  PortalId,
  TopicName,
  UUID,
  BrokerId,
  FrameId,
} from 'magpie-types';
import { getUUID } from '../utils';
import * as t from 'ts-schema';
import { ValidationSubscriber, ValidationError } from '../errors';

type MessageBuilderOptions<Message extends IMessage> = Pick<
  Message,
  'type' | 'payload'
> & {
  payloadSchema?: t.Schema<any>;
  replyType?: MessageType;
};

export class MessageBuilder<Message extends IMessage> {
  readonly #instance: Partial<Message> = {};
  readonly #payloadSchema: t.Schema<any> | undefined;

  constructor({
    type,
    payload,
    payloadSchema,
    replyType,
  }: MessageBuilderOptions<Message>) {
    this.#instance.id = getUUID();
    this.#instance.type = type;
    this.#instance.payload = payload;
    this.#instance.crossPortal = false;
    this.#instance.replyType = replyType;
    this.#payloadSchema = payloadSchema;
  }

  public setTopic = (topic: TopicName): MessageBuilder<Message> => {
    this.#instance.topic = topic;
    return this;
  };

  public setSender = (clientId: ClientId): MessageBuilder<Message> => {
    this.#instance.sender = clientId;
    return this;
  };

  public setDispatchedAt = (): MessageBuilder<Message> => {
    this.#instance.dispatchedAt = Date.now();
    return this;
  };

  public setCrossPortal = (
    isCrossPortalTopic: boolean
  ): MessageBuilder<Message> => {
    this.#instance.crossPortal = isCrossPortalTopic;
    return this;
  };

  public setSenderFrameId = (frameId: FrameId): MessageBuilder<Message> => {
    this.#instance.senderFrameId = frameId;
    return this;
  };

  public setSenderPortalId = (
    portalId: PortalId | null
  ): MessageBuilder<Message> => {
    this.#instance.senderPortalId = portalId;
    return this;
  };

  public setSenderBrokerId = (brokerId: BrokerId): MessageBuilder<Message> => {
    this.#instance.senderBrokerId = brokerId;
    return this;
  };

  public setAllowCrossTab = (
    allowCrossTab: boolean
  ): MessageBuilder<Message> => {
    this.#instance.allowCrossTab = allowCrossTab;
    return this;
  };

  public build = (): IMessage => {
    const requiredFields: Array<keyof IMessage> = [
      'type',
      'id',
      'sender',
      'topic',
      'dispatchedAt',
      'crossPortal',
      'senderPortalId',
      'allowCrossTab',
      'senderBrokerId',
      'senderFrameId',
    ];
    const missingField = requiredFields.find(
      (field) => this.#instance[field] === undefined
    );
    if (missingField) {
      throw new Error(
        `Failed to build message, field "${missingField}" was not prepared.`
      );
    }
    return this.#instance as IMessage;
  };

  public getType = (): MessageType => this.#instance.type!;

  public isPayloadValid = (): boolean => {
    if (!this.#payloadSchema) {
      return !this.#instance.payload;
    }
    return t.validate(this.#payloadSchema, this.#instance.payload);
  };

  public getReplyType = (): MessageType | undefined => this.#instance.replyType;

  public getId = (): string => this.#instance.id!;

  public setReplyTo = (messageId: UUID) => {
    this.#instance.replyTo = messageId;
    return this;
  };

  public validate = (onValidationError: ValidationSubscriber) => {
    if (this.isPayloadValid()) {
      return;
    }

    const validationError = new ValidationError(
      ValidationError.Reason.INVALID_PAYLOAD
    );
    onValidationError(validationError);
  };
}
